<!-- src/components/telemetry/StatisticsPanel.vue -->
<template>
  <div class="statistics-panel">
    <div class="stats-header">
      <h3>Statistiche Dettagliate</h3>
      <span class="last-update">
        Ultimo aggiornamento: {{ formatTimestamp(latestTimestamp) }}
      </span>
    </div>

    <div class="stats-grid">
      <!-- Statistiche Generali -->
      <div class="stats-section">
        <h4>Generali</h4>
        <div class="stats-cards">
          <div class="stat-card">
            <span class="stat-label">Record Totali</span>
            <span class="stat-value">{{ stats.total_records || 0 }}</span>
          </div>
          <div class="stat-card">
            <span class="stat-label">Veicoli Unici</span>
            <span class="stat-value">{{ stats.unique_vehicles || 0 }}</span>
          </div>
          <div class="stat-card">
            <span class="stat-label">Periodo Monitorato</span>
            <span class="stat-value">{{ formatPeriod() }}</span>
          </div>
        </div>
      </div>

      <!-- Velocità -->
      <div class="stats-section">
        <h4>Velocità</h4>
        <div class="stats-cards">
          <div class="stat-card">
            <span class="stat-label">Media</span>
            <span class="stat-value"
              >{{ calculateSpeedStats().avg.toFixed(1) }} km/h</span
            >
          </div>
          <div class="stat-card">
            <span class="stat-label">Massima</span>
            <span class="stat-value">{{ calculateSpeedStats().max }} km/h</span>
          </div>
          <div class="stat-card">
            <span class="stat-label">Minima</span>
            <span class="stat-value">{{ calculateSpeedStats().min }} km/h</span>
          </div>
        </div>
      </div>

      <!-- Motore -->
      <div class="stats-section">
        <h4>Motore</h4>
        <div class="stats-cards">
          <div class="stat-card">
            <span class="stat-label">RPM Medio</span>
            <span class="stat-value">{{
              calculateRpmStats().avg.toFixed(0)
            }}</span>
          </div>
          <div class="stat-card">
            <span class="stat-label">Temperatura</span>
            <span class="stat-value"
              >{{ latestData?.engine_temperature || 0 }}°C</span
            >
          </div>
          <div class="stat-card">
            <span class="stat-label">Carico</span>
            <span class="stat-value">{{ latestData?.engine_load || 0 }}%</span>
          </div>
        </div>
      </div>

      <!-- Carburante -->
      <div class="stats-section">
        <h4>Carburante</h4>
        <div class="fuel-stats">
          <div class="fuel-gauge-large">
            <div class="gauge-container">
              <div class="gauge-background"></div>
              <div
                class="gauge-fill"
                :style="{ height: `${latestData?.fuel_level || 0}%` }"
              ></div>
              <div class="gauge-text">{{ latestData?.fuel_level || 0 }}%</div>
            </div>
            <div class="fuel-info">
              <div class="info-item">
                <span class="info-label">Consumo stimato</span>
                <span class="info-value">-- L/100km</span>
              </div>
              <div class="info-item">
                <span class="info-label">Autonomia residua</span>
                <span class="info-value">-- km</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Informazioni Veicolo -->
      <div class="stats-section span-2">
        <h4>Informazioni Veicolo</h4>
        <div class="vehicle-info-grid">
          <div class="info-row">
            <span class="info-label">VIN:</span>
            <span class="info-value monospace">{{
              latestData?.vin || "N/D"
            }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Ultima posizione:</span>
            <span class="info-value">{{ formatLocation() }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Acceleratore:</span>
            <span class="info-value"
              >{{ latestData?.throttle_position || 0 }}%</span
            >
          </div>
          <div class="info-row">
            <span class="info-label">Tempo attivo:</span>
            <span class="info-value">{{ calculateUptime() }}</span>
          </div>
        </div>
      </div>
    </div>

    <div class="stats-footer">
      <button @click="exportData" class="export-btn">
        <span>📊</span> Esporta Dati
      </button>
      <span class="data-note">
        Dati aggiornati in tempo reale • Intervallo di polling: 30s
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";

const props = defineProps<{
  stats: {
    total_records: number;
    unique_vehicles: number;
    latest_timestamp: number;
    earliest_timestamp: number;
  };
  latestData: any;
}>();

const latestTimestamp = computed(
  () => props.stats?.latest_timestamp || props.latestData?.timestamp
);

function formatTimestamp(timestamp: number) {
  if (!timestamp) return "N/D";
  return new Date(timestamp * 1000).toLocaleString("it-IT");
}

function formatPeriod() {
  if (!props.stats?.earliest_timestamp || !props.stats?.latest_timestamp) {
    return "N/D";
  }

  const start = new Date(props.stats.earliest_timestamp * 1000);
  const end = new Date(props.stats.latest_timestamp * 1000);

  const diffMs = end.getTime() - start.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffHours = Math.floor(
    (diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
  );

  if (diffDays > 0) {
    return `${diffDays}g ${diffHours}h`;
  } else if (diffHours > 0) {
    return `${diffHours} ore`;
  } else {
    return "< 1 ora";
  }
}

function calculateSpeedStats() {
  // In una implementazione reale, qui calcoleresti le statistiche dai dati storici
  return {
    avg: props.latestData?.vehicle_speed || 0,
    max: props.latestData?.vehicle_speed || 0,
    min: props.latestData?.vehicle_speed || 0,
  };
}

function calculateRpmStats() {
  // In una implementazione reale, qui calcoleresti le statistiche dai dati storici
  return {
    avg: props.latestData?.engine_rpm || 0,
    max: props.latestData?.engine_rpm || 0,
    min: props.latestData?.engine_rpm || 0,
  };
}

function getBatteryClass(level: number) {
  if (level >= 70) return "high";
  if (level >= 30) return "medium";
  return "low";
}

function getBatteryStatus(level: number) {
  if (level >= 70) return "Ottimo";
  if (level >= 30) return "Medio";
  return "Basso";
}

function formatLocation() {
  if (!props.latestData?.latitude || !props.latestData?.longitude) {
    return "N/D";
  }
  return `${props.latestData.latitude.toFixed(
    6
  )}, ${props.latestData.longitude.toFixed(6)}`;
}

function calculateUptime() {
  // Esempio: calcola dal primo timestamp
  if (!props.stats?.earliest_timestamp) return "N/D";

  const start = new Date(props.stats.earliest_timestamp * 1000);
  // console.log("🚀 [calculateUptime] start:", start);
  const now = new Date();
  // console.log("🚀 [calculateUptime] now:", now);
  const diffMs = now.getTime() - start.getTime();

  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffHours = Math.floor(
    (diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
  );

  if (diffDays > 0) {
    return `${diffDays}g ${diffHours}h`;
  } else if (diffHours > 0) {
    return `${diffHours}h`;
  } else {
    return "< 1h";
  }
}

function exportData() {
  // Implementa l'esportazione dati
  console.log("Esportazione dati...");
  // Qui potresti implementare il download CSV/JSON
}
</script>

<style scoped>
.statistics-panel {
  background: #1a1a1a;
  border-radius: 8px;
  padding: 24px;
  color: white;
}

.stats-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #2a2f34;
}

.stats-header h3 {
  margin: 0;
  font-size: 22px;
  color: white;
}

.last-update {
  font-size: 14px;
  color: #9fb0c2;
  font-family: monospace;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0;
  margin-bottom: 0;
}

.stats-section {
  background: #1c2229;
  border: 1px solid #2a2f34;
  border-radius: 0;
  padding: 20px;
  margin: 0;
}

.stats-section.span-2 {
  grid-column: span 2;
}

.stats-section h4 {
  margin: 0 0 16px 0;
  font-size: 16px;
  color: #008f8f;
  border-bottom: 1px solid #2a2f34;
  padding-bottom: 12px;
  font-weight: 600;
}

.stats-cards {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

.stat-card {
  background: #222831;
  border: 1px solid #2a2f34;
  border-radius: 6px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
}

.stat-card:hover {
  background: #2a2f34;
  border-color: #3a3f44;
}

.stat-label {
  font-size: 12px;
  color: #9fb0c2;
  text-align: center;
}

.stat-value {
  font-size: 24px;
  font-weight: bold;
  color: white;
  text-align: center;
}

.fuel-stats {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
}

.fuel-gauge-large {
  display: flex;
  align-items: center;
  gap: 32px;
}

.gauge-container {
  position: relative;
  width: 120px;
  height: 200px;
  background: #222831;
  border: 2px solid #2a2f34;
  border-radius: 8px;
  overflow: hidden;
}

.gauge-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(to bottom, #ef4444, #f59e0b, #10b981);
  opacity: 0.15;
}

.gauge-fill {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  background: linear-gradient(to top, #f44336, #ff9800, #4caf50);
  transition: height 0.5s ease;
}

.gauge-text {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 24px;
  font-weight: bold;
  color: white;
  z-index: 2;
}

.fuel-info {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.battery-stats {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.battery-container {
  width: 200px;
  height: 80px;
  border: 3px solid #444;
  border-radius: 12px;
  padding: 8px;
  position: relative;
  display: flex;
  align-items: center;
}

.battery-container.high {
  border-color: #4caf50;
}
.battery-container.medium {
  border-color: #ff9800;
}
.battery-container.low {
  border-color: #f44336;
}

.battery-level {
  width: 100%;
  height: 100%;
  background: #2a2a2a;
  border-radius: 6px;
  overflow: hidden;
  position: relative;
}

.battery-fill {
  height: 100%;
  transition: width 0.5s ease;
}

.battery-container.high .battery-fill {
  background: #4caf50;
}
.battery-container.medium .battery-fill {
  background: #ff9800;
}
.battery-container.low .battery-fill {
  background: #f44336;
}

.battery-percentage {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 24px;
  font-weight: bold;
  color: white;
}

.battery-info {
  display: flex;
  gap: 8px;
  align-items: center;
}

.status-label {
  color: #aaa;
  font-size: 14px;
}

.status-value {
  font-size: 16px;
  font-weight: bold;
  padding: 4px 12px;
  border-radius: 4px;
}

.status-value.high {
  background: #4caf50;
  color: white;
}
.status-value.medium {
  background: #ff9800;
  color: white;
}
.status-value.low {
  background: #f44336;
  color: white;
}

.vehicle-info-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.info-row {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.info-item {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
}

.info-label {
  font-size: 14px;
  color: #9fb0c2;
}

.info-value {
  font-size: 16px;
  color: white;
}

.info-value.monospace {
  font-family: monospace;
  background: #222831;
  padding: 4px 8px;
  border-radius: 4px;
  border: 1px solid #2a2f34;
}

.stats-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 20px;
  border-top: 1px solid #2a2f34;
}

.export-btn {
  background: #008f8f;
  border: none;
  color: white;
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
  font-weight: 600;
}

.export-btn:hover {
  background: #00a7a7;
  transform: translateY(-1px);
}

.data-note {
  font-size: 12px;
  color: #9fb0c2;
  font-style: italic;
}

@media (max-width: 1200px) {
  .stats-grid {
    grid-template-columns: 1fr;
  }

  .stats-section.span-2 {
    grid-column: span 1;
  }

  .fuel-gauge-large {
    flex-direction: column;
  }
}

@media (max-width: 768px) {
  .stats-cards {
    grid-template-columns: 1fr;
  }

  .vehicle-info-grid {
    grid-template-columns: 1fr;
  }

  .stats-footer {
    flex-direction: column;
    gap: 16px;
    align-items: flex-start;
  }
}
</style>
